// src/pages/TaskDashboard/TaskDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi, Task, DashboardStats as DashboardStatsType } from '../../api/tasks';
import { visibilityApi, type TeamMember } from '../../api/todo';
import { useAuth } from '../../contexts/AuthContext';
import { useUserInfo } from '../../hooks/useUserInfo';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { Popup } from '../../components/Popup';
import TaskCreate from '../Task/TaskCreate';
import TaskHeatmapCalendar, { type DayDatum } from '../../components/TaskHeatmapCalendar';

const ELEVATED = new Set(['admin', 'owner', 'superadmin', 'founder', 'director']);
// Supervisor is a view-only tier and cannot filter the dashboard by team employee.
const MANAGER_ROLES = new Set(['manager', 'team lead', 'hod', 'hr']);

export function TaskDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isAdmin = ELEVATED.has(role);
  const isManager = MANAGER_ROLES.has(role);
  const canFilterByEmployee = isAdmin || isManager;

  const { currentUser, getUserName } = useUserInfo();
  const { showError, popup, closePopup } = usePermissionGuard();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<DashboardStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [myUpcomingTasks, setMyUpcomingTasks] = useState<Task[]>([]);
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Load team members for filter (admin/manager only)
  useEffect(() => {
    if (!canFilterByEmployee) return;
    visibilityApi.team()
      .then((r) => setTeamMembers(r.data.items || []))
      .catch(() => {});
  }, [canFilterByEmployee]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = selectedEmployee ? { assigned_to: selectedEmployee, page_size: 100 } : { page_size: 100 };
      const tasksResponse = await tasksApi.list(filters);
      const allTasks = tasksResponse.data.items || [];
      setTasks(allTasks);

      const statsResponse = await tasksApi.getDashboardStats();
      setStats(statsResponse.data);

      if (!selectedEmployee) {
        const myTasksResponse = await tasksApi.getMyTasks(false);
        const upcoming = (myTasksResponse.data.items || [])
          .filter((t) => t.due_date && new Date(t.due_date) > new Date())
          .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
          .slice(0, 5);
        setMyUpcomingTasks(upcoming);
      } else {
        const upcoming = allTasks
          .filter((t) => t.due_date && new Date(t.due_date) > new Date())
          .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
          .slice(0, 5);
        setMyUpcomingTasks(upcoming);
      }

      const recent = [...allTasks]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
      setRecentTasks(recent);
    } catch (err: any) {
      showError('DASHBOARD_ERROR', err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const getStatusStats = () => {
    const statusCounts = {
      'TODO': tasks.filter(t => t.status === 'TODO').length,
      'IN_PROGRESS': tasks.filter(t => t.status === 'IN_PROGRESS').length,
      'IN_REVIEW': tasks.filter(t => t.status === 'IN_REVIEW').length,
      'BLOCKED': tasks.filter(t => t.status === 'BLOCKED').length,
      'DONE': tasks.filter(t => t.status === 'DONE').length,
      'CANCELLED': tasks.filter(t => t.status === 'CANCELLED').length,
    };
    const total = tasks.length;
    const completed = statusCounts.DONE;
    const progress = total > 0 ? (completed / total) * 100 : 0;
    return { statusCounts, total, completed, progress };
  };

  const handleStatusClick = (status: string) => {
    navigate(`/tasks/list?status=${status}`);
  };

  const handlePriorityClick = (priority: string) => {
    navigate(`/tasks/list?priority=${priority}`);
  };

  const statusStats = getStatusStats();
  const statusColors = {
    'TODO': 'var(--gt-gray-500)',
    'IN_PROGRESS': 'var(--gt-blue-600)',
    'IN_REVIEW': 'var(--gt-violet-600)',
    'BLOCKED': 'var(--gt-red-600)',
    'DONE': 'var(--gt-emerald-600)',
    'CANCELLED': 'var(--gt-gray-500)',
  };

  const statusLabels = {
    'TODO': 'To Do',
    'IN_PROGRESS': 'In Progress',
    'IN_REVIEW': 'In Review',
    'BLOCKED': 'Hold',
    'DONE': 'Done',
    'CANCELLED': 'Cancelled',
  };

  const priorityColors = {
    URGENT: 'bg-red-500',
    HIGH: 'bg-orange-500',
    MEDIUM: 'bg-yellow-500',
    LOW: 'bg-green-500',
  };

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>dashboard</em>…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gt-themed">
      <div className="gt-shell min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <h1 className="gt-page-title">
              Welcome <em>back</em>, {currentUser?.name?.split(' ')[0] || 'friend'}.
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              {selectedEmployee
                ? <>Viewing tasks for <em className="gt-serif-italic">{teamMembers.find((m) => m.user_id === selectedEmployee)?.name || selectedEmployee}</em></>
                : <>Here's what's happening <em className="gt-serif-italic">today</em>.</>}
            </p>
          </div>
            <div className="flex items-center gap-2">
              {canFilterByEmployee && teamMembers.length > 0 && (
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="gt-select"
                  style={{ width: 'auto', padding: '8px 12px', fontSize: 12.5, minWidth: 180 }}
                >
                  <option value="">All Employees</option>
                  {teamMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name || m.email || m.user_id}
                    </option>
                  ))}
                </select>
              )}
              <button onClick={() => setIsCreateModalOpen(true)} className="gt-btn is-primary">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Task
              </button>
            </div>
          </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-blue-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/tasks/list')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Tasks</p>
                <p className="text-3xl font-bold text-gray-900">{statusStats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-blue-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${statusStats.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">{statusStats.completed} Completed</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-yellow-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleStatusClick('IN_PROGRESS')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-3xl font-bold text-yellow-600">{statusStats.statusCounts.IN_PROGRESS}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Actively being worked on</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-purple-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleStatusClick('IN_REVIEW')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Needs Review</p>
                <p className="text-3xl font-bold text-purple-600">{statusStats.statusCounts.IN_REVIEW}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Awaiting approval</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-red-500 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleStatusClick('BLOCKED')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Hold</p>
                <p className="text-3xl font-bold text-red-600">{statusStats.statusCounts.BLOCKED}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">Need attention</p>
          </div>
        </div>

        {/* Task Distribution Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Task Distribution</h3>
            </div>
            <div className="space-y-4">
              {Object.entries(statusStats.statusCounts).map(([status, count]) => (
                <div key={status} className="cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors" onClick={() => handleStatusClick(status)}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium" style={{ color: statusColors[status as keyof typeof statusColors] }}>
                      {statusLabels[status as keyof typeof statusLabels]}
                    </span>
                    <span className="text-gray-600">{count} tasks</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${statusStats.total > 0 ? (count / statusStats.total) * 100 : 0}%`,
                        backgroundColor: statusColors[status as keyof typeof statusColors]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Quick Stats</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Completion Rate</span>
                <span className="font-bold text-green-600">{Math.round(statusStats.progress)}%</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Open Tasks</span>
                <span className="font-bold text-blue-600">{statusStats.total - statusStats.statusCounts.DONE - statusStats.statusCounts.CANCELLED}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Overdue</span>
                <span className="font-bold text-red-600">{stats?.overdue || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Due Today</span>
                <span className="font-bold text-orange-600">{stats?.due_today || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Unassigned</span>
                <span className="font-bold text-purple-600">{stats?.unassigned || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Priority Breakdown and Upcoming Tasks */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Priority Breakdown</h3>
            </div>
            <div className="space-y-3">
              {stats?.by_priority && Object.entries(stats.by_priority).map(([priority, count]) => (
                <div key={priority} className="cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors" onClick={() => handlePriorityClick(priority)}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{priority}</span>
                    <span className="text-gray-600">{count} tasks</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`${priorityColors[priority as keyof typeof priorityColors]} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${(count / (stats?.open || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <TaskHeatmapCalendar
            data={(() => {
              const buckets: Record<string, number> = {};
              for (const t of tasks) {
                const d = (t.due_date || '').slice(0, 10);
                if (!d) continue;
                buckets[d] = (buckets[d] || 0) + 1;
              }
              return Object.entries(buckets).map(([date, count]) => ({
                date,
                heat: Math.min(5, Math.max(1, Math.ceil(count / 2))),
                hasEvent: true,
              })) as DayDatum[];
            })()}
          />

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Your Upcoming Tasks</h3>
            </div>
            {myUpcomingTasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No upcoming tasks</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myUpcomingTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{task.title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                        task.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        task.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </div>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-green-500 h-1 rounded-full"
                        style={{ width: `${task.progress_percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Recent Tasks</h3>
            <button 
              onClick={() => navigate('/tasks/list')}
              className="ml-auto text-sm text-blue-600 hover:text-blue-700"
            >
              View all →
            </button>
          </div>
          {recentTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No recent tasks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{task.title}</p>
                    {canFilterByEmployee && task.primary_assignee_id && (
                      <p className="text-xs text-indigo-600 mt-0.5">
                        Assigned to: {getUserName(task.primary_assignee_id)}
                      </p>
                    )}
                    <div className="flex gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        task.status === 'TODO' ? 'bg-gray-100 text-gray-700' :
                        task.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                        task.status === 'IN_REVIEW' ? 'bg-purple-100 text-purple-700' :
                        task.status === 'BLOCKED' ? 'bg-red-100 text-red-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.status.replace(/_/g, ' ')}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        task.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                        task.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        task.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                    </p>
                    <div className="w-24 mt-1 bg-gray-200 rounded-full h-1">
                      <div 
                        className="bg-green-500 h-1 rounded-full"
                        style={{ width: `${task.progress_percent}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <TaskCreate
  isOpen={isCreateModalOpen}
  onClose={() => setIsCreateModalOpen(false)}
  onSuccess={() => loadDashboardData()}
/>

      <Popup
        isOpen={popup.isOpen}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
      </div>
    </div>
  );
}

export default TaskDashboard;